import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarCheck, Loader2, AlertCircle, CheckCircle2,
  Coffee, Package, X, MapPin,
} from "lucide-react";
import {
  useLazyScannerLookupQuery,
  useScannerMarkAttendanceMutation,
  useScannerMarkRefreshmentMutation,
  useScannerUpdateMaterialsMutation,
} from "../api/adminEventsApi";

function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "An error occurred";
}

export default function ScanActionPage() {
  const { barcode } = useParams<{ barcode: string }>();

  const [triggerLookup, { isFetching }] = useLazyScannerLookupQuery();
  const [markAttendance,  { isLoading: markingAtt }] = useScannerMarkAttendanceMutation();
  const [markRefreshment, { isLoading: markingRef }] = useScannerMarkRefreshmentMutation();
  const [updateMaterials] = useScannerUpdateMaterialsMutation();

  const [reg, setReg]             = useState<any | null>(null);
  const [loadError, setLoadError] = useState("");
  const [busyMat, setBusyMat]     = useState<number | null>(null);
  const [photoBroken, setPhotoBroken] = useState(false);
  const [toast, setToast]         = useState<{ type: "success" | "error"; text: string } | null>(null);

  const showToast = (type: "success" | "error", text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  /**
   * Re-read the registration from the server.
   *
   * The second argument to a lazy-query trigger is `preferCacheValue`, and this
   * used to pass `true` — so every reload after an action handed back the
   * cached payload from BEFORE that action. Marking someone present or issuing
   * a pack appeared to do nothing until the page was reloaded by hand. It is
   * omitted deliberately: this function is only ever called to see a change.
   */
  const reload = async () => {
    if (!barcode) return;
    setLoadError("");
    setPhotoBroken(false);
    try {
      const data = await triggerLookup(barcode).unwrap();
      setReg(data);
    } catch (err: any) {
      setLoadError(extractError(err));
    }
  };

  useEffect(() => { reload(); }, [barcode]);

  const handleMarkAttendance = async (dayId: number) => {
    try {
      await markAttendance({ barcode: barcode!, event_day_id: dayId }).unwrap();
      showToast("success", "Attendance marked.");
      reload();
    } catch (err) { showToast("error", extractError(err)); }
  };

  const handleMarkRefreshment = async (dayId: number) => {
    try {
      await markRefreshment({ barcode: barcode!, event_day_id: dayId }).unwrap();
      showToast("success", "Refreshment collected.");
      reload();
    } catch (err) { showToast("error", extractError(err)); }
  };

  const handleIssueMaterial = async (eventMaterialId: number) => {
    if (!reg) return;
    const regId = reg.id ?? reg.registration_id;
    setBusyMat(eventMaterialId);
    try {
      await updateMaterials({
        registrationId: regId,
        material_updates: [{ event_material_id: eventMaterialId, is_collected: true }],
      }).unwrap();
      showToast("success", "Material issued.");
      reload();
    } catch (err) { showToast("error", extractError(err)); }
    setBusyMat(null);
  };

  const days: any[]         = reg?.event?.days ?? [];
  const attendance: any[]   = reg?.attendance ?? [];
  const materials: any[]    = reg?.materials ?? [];
  const refreshments: any[] = reg?.refreshment_choices ?? [];

  // Unsettled fee — the API refuses check-in, food and materials for this one.
  const paymentBlocked      = !!reg?.payment_blocked;

  const attendancePending   = attendance.some((a: any) => !a.is_present);
  // refreshment_choices is now per-day: { event_day_id, day_label, chosen_name, available_options, is_collected }
  const refreshmentPending  = refreshments.some((c: any) => !c.is_collected);
  const materialPending     = materials.some((m: any) => !m.is_collected);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isFetching && !reg) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto" />
          <p className="text-sm font-bold text-gray-500">Loading registration…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 max-w-sm w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-rose-500" />
          </div>
          <h2 className="font-black text-gray-900 text-lg">Invalid QR Code</h2>
          <p className="text-sm text-gray-500 font-medium">{loadError}</p>
          <button
            onClick={reload}
            className="w-full bg-gray-900 text-white font-black text-sm py-3 rounded-2xl hover:bg-gray-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!reg) return null;

  return (
    // The page is opened by phone at the door, so it is laid out as a single
    // column of cards and capped so it does not stretch into a band of
    // whitespace on the desk machine the same QR gets scanned from.
    <div className="max-w-2xl mx-auto pb-10 space-y-4">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 flex items-center gap-3 px-5 py-4 rounded-2xl shadow-xl text-sm font-bold transition-all ${
          toast.type === "success"
            ? "bg-emerald-600 text-white"
            : "bg-rose-600 text-white"
        }`}>
          {toast.type === "success"
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle className="w-4 h-4 shrink-0" />}
          <span className="flex-1">{toast.text}</span>
          <button onClick={() => setToast(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gray-900 rounded-3xl px-5 sm:px-6 pt-6 pb-6">
        <div className="text-[9px] font-black uppercase tracking-[0.25em] text-white/40 mb-2">
          NIQS Event Scanner
        </div>
        <div className="flex items-center gap-4">
          {/* The holder's photograph, so the desk can check the card against
              the person presenting it. Initials stand in for a guest, who has
              no account and so no photograph on file. */}
          {reg.photo_url && !photoBroken ? (
            <img
              src={reg.photo_url}
              alt={reg.attendee_name ?? "Attendee"}
              onError={() => setPhotoBroken(true)}
              className="w-14 h-14 rounded-2xl object-cover shrink-0 border-2 border-indigo-500 shadow-lg shadow-indigo-900/40"
            />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-indigo-500 flex items-center justify-center text-white font-black text-lg shrink-0 shadow-lg shadow-indigo-900/40">
              {reg.attendee_name?.substring(0, 2).toUpperCase() ?? "—"}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-base sm:text-lg font-black text-white leading-snug break-words">{reg.attendee_name}</div>
            <div className="text-xs font-bold text-white/50 truncate">{reg.attendee_email}</div>
            <div className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mt-0.5">
              {reg.registration_no}
            </div>
          </div>
        </div>

        {/* Event info */}
        {reg.event && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5">
            <div className="font-black text-white text-sm">{reg.event.title}</div>
            <div className="flex flex-wrap gap-3">
              {reg.event.location && (
                <span className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
                  <MapPin className="w-3 h-3" /> {reg.event.location}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Status chips */}
        <div className="flex flex-wrap gap-2 mt-4">
          <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border ${
            reg.overall_attendance === "present"
              ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
              : reg.overall_attendance === "partial"
              ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
              : "bg-white/10 text-white/50 border-white/10"
          }`}>
            Attendance: {reg.overall_attendance ?? "absent"}
          </span>
          <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border ${
            reg.payment_status === "paid" || reg.payment_status === "free"
              ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
              : "bg-amber-500/20 text-amber-300 border-amber-500/30"
          }`}>
            {reg.payment_status}
          </span>
        </div>
      </div>

      <div className="space-y-4">

        {/* Unpaid — every action below is refused by the API */}
        {paymentBlocked && (
          <div className="bg-rose-50 border border-rose-100 rounded-3xl p-4 sm:p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-black text-rose-900 text-sm">Payment outstanding</div>
              <p className="text-xs font-medium text-rose-800 mt-0.5">
                {reg.amount_due != null ? `₦${Number(reg.amount_due).toLocaleString()} is unpaid. ` : ""}
                Check-in, food and materials stay locked until the secretariat records the payment.
              </p>
            </div>
          </div>
        )}

        {/* ── Attendance ────────────────────────────────────────────────────── */}
        {days.length > 0 && (
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-2xl flex items-center justify-center shrink-0 ${attendancePending ? "bg-amber-50" : "bg-emerald-50"}`}>
                  <CalendarCheck className={`w-4 h-4 ${attendancePending ? "text-amber-500" : "text-emerald-500"}`} />
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">Attendance</div>
              </div>
              <div className="text-xs font-black text-gray-500">
                {attendance.filter((a: any) => a.is_present).length}/{days.length} present
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {days.map((day: any) => {
                const rec = attendance.find((a: any) => a.event_day_id === day.id || a.day_id === day.id);
                const isPresent = !!rec?.is_present;
                return (
                  <div key={day.id} className={`flex items-center justify-between gap-3 px-4 sm:px-5 py-4 transition-colors ${isPresent ? "bg-emerald-50" : "bg-white"}`}>
                    <div className="min-w-0">
                      <div className={`font-bold text-sm ${isPresent ? "text-emerald-800" : "text-gray-800"}`}>{day.day_label}</div>
                      <div className="text-xs text-gray-400 font-medium">{day.day_date}</div>
                    </div>
                    {isPresent ? (
                      <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Present
                      </span>
                    ) : (
                      <button
                        onClick={() => handleMarkAttendance(day.id)}
                        disabled={markingAtt || paymentBlocked}
                        className="flex items-center gap-1.5 bg-gray-900 text-white font-black text-xs px-4 py-2.5 rounded-xl active:scale-95 transition-transform disabled:opacity-50 shrink-0"
                      >
                        {markingAtt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarCheck className="w-3.5 h-3.5" />}
                        Mark Present
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Refreshments ──────────────────────────────────────────────────── */}
        {/* refreshment_choices is per-day: { event_day_id, day_label, chosen_name, available_options, is_collected } */}
        {refreshments.length > 0 && (
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-2xl flex items-center justify-center shrink-0 ${refreshmentPending ? "bg-amber-50" : "bg-emerald-50"}`}>
                  <Coffee className={`w-4 h-4 ${refreshmentPending ? "text-amber-500" : "text-emerald-500"}`} />
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">Refreshments</div>
              </div>
              <div className="text-xs font-black text-gray-500">
                {refreshments.filter((c: any) => c.is_collected).length}/{refreshments.length} issued
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {refreshments.map((dayRef: any) => (
                <div key={dayRef.event_day_id} className={`flex items-center justify-between gap-3 px-4 sm:px-5 py-4 transition-colors ${dayRef.is_collected ? "bg-emerald-50" : "bg-white"}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <div className={`font-bold text-sm ${dayRef.is_collected ? "text-emerald-800" : "text-gray-800"}`}>{dayRef.day_label}</div>
                    {dayRef.chosen_name ? (
                      <div className="text-xs font-bold text-indigo-600 mt-0.5">Choice: {dayRef.chosen_name}</div>
                    ) : (
                      <div className="text-xs text-gray-400 font-medium mt-0.5">
                        {(dayRef.available_options ?? []).map((o: any) => o.name).join(" · ")}
                      </div>
                    )}
                    {dayRef.is_collected && dayRef.collected_at && (
                      <div className="text-[10px] text-emerald-600 font-bold mt-0.5">Issued {dayRef.collected_at}</div>
                    )}
                  </div>
                  {dayRef.is_collected ? (
                    <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Issued
                    </span>
                  ) : (
                    <button
                      onClick={() => handleMarkRefreshment(dayRef.event_day_id)}
                      disabled={markingRef || paymentBlocked}
                      className="flex items-center gap-1.5 bg-rose-600 text-white font-black text-xs px-4 py-2.5 rounded-xl active:scale-95 transition-transform disabled:opacity-50 shrink-0"
                    >
                      {markingRef ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coffee className="w-3.5 h-3.5" />}
                      Collect
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Materials ─────────────────────────────────────────────────────── */}
        {materials.length > 0 && (
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-2xl flex items-center justify-center shrink-0 ${materialPending ? "bg-amber-50" : "bg-emerald-50"}`}>
                  <Package className={`w-4 h-4 ${materialPending ? "text-amber-500" : "text-emerald-500"}`} />
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">Materials</div>
              </div>
              <div className="text-xs font-black text-gray-500">
                {materials.filter((m: any) => m.is_collected).length}/{materials.length} issued
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {materials.map((m: any) => {
                const busy = busyMat === m.id;
                return (
                  <div key={m.id} className={`flex items-center justify-between gap-3 px-4 sm:px-5 py-4 transition-colors ${m.is_collected ? "bg-emerald-50" : "bg-white"}`}>
                    <div className="flex-1 min-w-0 mr-4">
                      <div className={`font-bold text-sm flex items-center gap-2 ${m.is_collected ? "text-emerald-800" : "text-gray-800"}`}>
                        {m.is_collected && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                        {m.name}
                      </div>
                      {m.description && <div className="text-xs text-gray-400 font-medium">{m.description}</div>}
                      {m.quantity_total != null && !m.is_collected && (
                        <div className={`text-[10px] font-bold mt-0.5 ${m.quantity_remaining === 0 ? "text-rose-600" : "text-gray-400"}`}>
                          {m.quantity_remaining} of {m.quantity_total} remaining{m.quantity_remaining === 0 ? " — DEPLETED" : ""}
                        </div>
                      )}
                      {m.is_collected && m.collected_at && (
                        <div className="text-[10px] text-emerald-600 font-bold mt-0.5">Issued {m.collected_at}</div>
                      )}
                    </div>
                    {m.is_collected ? (
                      <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                        Issued ✓
                      </span>
                    ) : (
                      <button
                        onClick={() => handleIssueMaterial(m.id)}
                        disabled={busy || paymentBlocked}
                        className="flex items-center gap-1.5 bg-amber-500 text-white font-black text-xs px-4 py-2.5 rounded-xl active:scale-95 transition-transform disabled:opacity-50 shrink-0"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                        Issue
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All done */}
        {!attendancePending && !refreshmentPending && !materialPending && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <div className="font-black text-emerald-800 text-sm">All items collected for this attendee.</div>
          </div>
        )}

        {/* Footer */}
        <div className="bg-gray-900 rounded-3xl p-5 text-center">
          <div className="text-[9px] font-black uppercase tracking-widest text-white/30 mb-1">NIQS Portal</div>
          <div className="text-xs font-bold text-white/50">Official Event Management System</div>
        </div>
      </div>
    </div>
  );
}
